import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Tippy, { type TippyProps } from "@tippyjs/react";
import type { Instance } from "tippy.js";

import { appendToTippyContainer } from "../../utils/tippy";
import "../tooltip/tooltip_tippy.css";

/**
 * モーダル（1000）より下、ページ内の固定バー（900）より上。カード内の添付チップ
 * から開くプレビューモーダルを、カードの上に重ねるため Tippy 既定（9999）から下げる。
 */
const HOVER_CARD_Z_INDEX = 950;

/** ホバーできるポインタがある環境か（タッチのみの端末ではカードを出さない） */
const canHover = (): boolean =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

const hasOpenDialog = (): boolean =>
  !!document.querySelector('[role="dialog"]');

interface HoverCardProps {
  /** カードの中身。開いている間だけマウントされる */
  content: ReactNode;
  /** ホバー対象（ref を受け取れる単一要素） */
  children: ReactElement;
  placement?: TippyProps["placement"];
  /** true の間はカードを開かない（ドラッグ中など） */
  disabled?: boolean;
  /** スクロール領域内での位置ズレ対策（`strategy: "fixed"` 等） */
  popperOptions?: TippyProps["popperOptions"];
}

/**
 * 要素にホバーすると白パネルのカードを開く汎用ラッパー。ToolTip（文字列の
 * 補足説明）では収まらない、Markdown 本文や複数項目を覗き見させる用途向け。
 *
 * - カード上へポインタを移しても閉じない（interactive）ので、中身のスクロールや
 *   リンク・チップの操作ができる。中身のホバーカード（ショットコメントリンク等）
 *   はこのカードの中へ入れ子で出るため、そちらへ移っても閉じない
 * - 中身から開いたモーダル（添付のプレビュー等）はカードの React ツリーに
 *   属するため、モーダル表示中はカードを閉じない（閉じると中身ごとモーダルが
 *   消える）。モーダルが閉じた後、最初のポインタ移動でカードの外にいれば閉じる
 * - 行をなぞっただけで次々と開かないよう、表示に少し遅延を入れる
 * - ホバーできない端末（タッチのみ）ではラップせず、子要素をそのまま返す
 */
const HoverCard = ({
  content,
  children,
  placement = "right-start",
  disabled = false,
  popperOptions,
}: HoverCardProps) => {
  const [contentMounted, setContentMounted] = useState(false);
  // モーダルが閉じるのを待つ監視の後始末（null = 監視していない）
  const stopDialogWatchRef = useRef<(() => void) | null>(null);

  useEffect(() => () => stopDialogWatchRef.current?.(), []);

  // Tippy は閉じる判定の前にポインタ移動の監視を外すため、ここで閉じるのを
  // 止めたら、モーダルが閉じた後に hideWithInteractivity で監視をかけ直す。
  const handleHide = useCallback((instance: Instance): false | void => {
    if (!hasOpenDialog()) return;
    if (!stopDialogWatchRef.current) {
      // eslint-disable-next-line no-unused-vars
      let rearm: ((e: MouseEvent) => void) | null = null;
      const observer = new MutationObserver(() => {
        if (hasOpenDialog() || rearm) return;
        observer.disconnect();
        rearm = (e: MouseEvent) => {
          stopDialogWatchRef.current?.();
          instance.hideWithInteractivity(e);
        };
        document.addEventListener("mousemove", rearm);
      });
      observer.observe(document.body, { childList: true, subtree: true });
      stopDialogWatchRef.current = () => {
        observer.disconnect();
        if (rearm) document.removeEventListener("mousemove", rearm);
        stopDialogWatchRef.current = null;
      };
    }
    return false;
  }, []);

  if (!canHover()) return children;

  return (
    <Tippy
      content={contentMounted ? content : <span />}
      theme="hover-card"
      arrow={false}
      interactive
      interactiveBorder={8}
      appendTo={appendToTippyContainer}
      zIndex={HOVER_CARD_Z_INDEX}
      delay={[300, 100]}
      duration={[150, 150]}
      placement={placement}
      offset={[0, 6]}
      maxWidth="none"
      touch={false}
      onShow={() => setContentMounted(true)}
      onHide={handleHide}
      onHidden={() => {
        stopDialogWatchRef.current?.();
        setContentMounted(false);
      }}
      disabled={disabled}
      {...(popperOptions ? { popperOptions } : {})}
    >
      {children}
    </Tippy>
  );
};

export default HoverCard;
